"use client"

import type React from "react"

import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Loader2 } from "lucide-react"
import { useAuth } from "@/hooks/use-auth"

interface BillingPlan {
  id: string
  name: string
  display_name: string
  billing_channel: string
  status: string
}

interface CreateTenantFormProps {
  onSuccess?: () => void
}

export function CreateTenantForm({ onSuccess }: CreateTenantFormProps = {}) {
  const { fetchWithAuth } = useAuth()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<{ tenant: { id: string; name: string }; tempPassword: string; adminEmail: string; planName: string | null } | null>(null)
  const [plans, setPlans] = useState<BillingPlan[]>([])
  const [plansLoading, setPlansLoading] = useState(true)

  const [formData, setFormData] = useState({
    tenant_name: "",
    admin_email: "",
    admin_name: "",
    billing_channel: "stripe",
    plan_id: "",
  })

  useEffect(() => {
    async function loadPlans() {
      try {
        const response = await fetchWithAuth("/api/billing/plans")
        if (response.ok) {
          const data = await response.json()
          const activePlans: BillingPlan[] = data.plans ?? []
          setPlans(activePlans)
          // Default to free plan matching default channel
          const freePlan = activePlans.find((p: BillingPlan) => p.name === "free-stripe")
          if (freePlan) {
            setFormData((prev) => ({ ...prev, plan_id: freePlan.id }))
          }
        }
      } catch {
        // Plans loading is best-effort; tenant can still be created with default free plan
      } finally {
        setPlansLoading(false)
      }
    }
    loadPlans()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchWithAuth is stable from useAuth
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    setSuccess(null)

    try {
      const response = await fetchWithAuth("/api/tenants/create", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          tenant_name: formData.tenant_name,
          admin_email: formData.admin_email,
          admin_name: formData.admin_name,
          billing_channel: formData.billing_channel,
          ...(formData.plan_id ? { plan_id: formData.plan_id } : {}),
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || "Failed to create tenant")
      }

      setSuccess({
        tenant: data.tenant,
        tempPassword: data.temp_password,
        adminEmail: formData.admin_email,
        planName: data.plan?.display_name ?? data.plan?.name ?? null,
      })

      // Reset form (keep plan_id for next creation)
      setFormData((prev) => ({
        tenant_name: "",
        admin_email: "",
        admin_name: "",
        billing_channel: prev.billing_channel,
        plan_id: prev.plan_id,
      }))
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to create tenant")
    } finally {
      setLoading(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Tenant Information</CardTitle>
        <CardDescription>Enter the details for the new organization and its admin user</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Tenant Name */}
          <div className="space-y-2">
            <Label htmlFor="tenant_name">Organization Name</Label>
            <Input
              id="tenant_name"
              type="text"
              placeholder="Acme Corporation"
              value={formData.tenant_name}
              onChange={(e) => setFormData({ ...formData, tenant_name: e.target.value })}
              required
              disabled={loading}
            />
          </div>

          {/* Admin Email */}
          <div className="space-y-2">
            <Label htmlFor="admin_email">Admin Email</Label>
            <Input
              id="admin_email"
              type="email"
              placeholder="admin@acme.com"
              value={formData.admin_email}
              onChange={(e) => setFormData({ ...formData, admin_email: e.target.value })}
              required
              disabled={loading}
            />
          </div>

          {/* Admin Name */}
          <div className="space-y-2">
            <Label htmlFor="admin_name">Admin Name</Label>
            <Input
              id="admin_name"
              type="text"
              placeholder="John Doe"
              value={formData.admin_name}
              onChange={(e) => setFormData({ ...formData, admin_name: e.target.value })}
              required
              disabled={loading}
            />
          </div>

          {/* Billing Channel */}
          <div className="space-y-2">
            <Label htmlFor="billing_channel">Billing Channel</Label>
            <Select
              value={formData.billing_channel}
              onValueChange={(value) => setFormData({ ...formData, billing_channel: value, plan_id: "" })}
              disabled={loading}
            >
              <SelectTrigger id="billing_channel">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="stripe">Stripe</SelectItem>
                <SelectItem value="shopify">Shopify</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">Determines payment provider and available plans. Cannot be changed later.</p>
          </div>

          {/* Billing Plan */}
          <div className="space-y-2">
            <Label htmlFor="plan_id">Billing Plan</Label>
            {plansLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Loading plans...</span>
              </div>
            ) : (() => {
              const channelPlans = plans.filter((p) => p.billing_channel === formData.billing_channel && p.status !== 'draft')
              return channelPlans.length > 0 ? (
                <Select
                  value={formData.plan_id}
                  onValueChange={(value) => setFormData({ ...formData, plan_id: value })}
                  disabled={loading}
                >
                  <SelectTrigger id="plan_id">
                    <SelectValue placeholder="Select a plan" />
                  </SelectTrigger>
                  <SelectContent>
                    {channelPlans.map((plan) => (
                      <SelectItem key={plan.id} value={plan.id}>
                        {plan.display_name} <span className="text-muted-foreground">({plan.name})</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-sm text-muted-foreground"><span>No plans available for</span> {formData.billing_channel} <span>channel.</span></p>
              )
            })()}
          </div>

          {/* Error Alert */}
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Success Alert */}
          {success && (
            <Alert>
              <AlertDescription>
                <div className="space-y-2">
                  <p className="font-semibold">Tenant created successfully!</p>
                  <p>
                    <strong>Organization:</strong> {success.tenant.name}
                  </p>
                  {success.planName && (
                    <p>
                      <strong>Plan:</strong> {success.planName}
                    </p>
                  )}
                  <p>
                    <strong>Admin Login:</strong> {success.adminEmail}
                  </p>
                  <p>
                    <strong>Temporary Password:</strong>{" "}
                    <code className="bg-muted px-2 py-1 rounded font-mono text-sm select-all">{success.tempPassword}</code>
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Please save this password securely and send it to the admin user. The admin can log in
                    immediately with these credentials.
                  </p>
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* Submit Button or Done Button */}
          {success ? (
            <Button type="button" onClick={onSuccess} className="w-full">
              Done
            </Button>
          ) : (
            <Button type="submit" disabled={loading} className="w-full">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              <span>Create Tenant</span>
            </Button>
          )}
        </form>
      </CardContent>
    </Card>
  )
}
